import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  getUserDisabilities,
  deleteUserDisability,
  getDisabilities,
  getDisabilityLevels,
  updateUserFile,
} from "../../../store/profile/actions";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import NoResults from "../../common/components/NoResults";
import DisabilityForm from "./DisabilityForm";
import AdvertSkeleton from "../../common/components/AdvertSkeleton";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import AlertDialog from "../components/AlertDialog";
import PreviewPdfUrl from "../../common/components/PreviewPdfUrl";
import LockIcon from "@mui/icons-material/Lock";
import moment from "moment";

export const Disabilities = (props) => {
  const {
    userProfile,
    env,
    loading,
    onWait,
    userDisabilities,
    getUserDisabilities,
    deleteUserDisability,
    getDisabilities,
    getDisabilityLevels,
    lookup,
    updateUserFile,
    canEdit,
  } = props;

  useEffect(() => {
    getUserDisabilities(userProfile.id);

    if (!lookup?.disabilityLevels?.length) getDisabilityLevels();
    if (!lookup?.disabilities?.length) getDisabilities();
  }, []);

  const [loadingDocument, setLoadingDocument] = useState(false);
  const [showDisabilityForm, setShowDisabilityForm] = useState(false);
  const [selectedDisability, setSelectedDisability] = useState(null);

  return (
    <>
      <Card className={`border rounded mb-3`} elevation={1} id="disabilities">
        <CardContent>
          {loading && !onWait && !userDisabilities.length ? (
            <AdvertSkeleton />
          ) : (
            <div className="container-fluid">
              <div className="d-flex flex-row justify-content-between align-items-center bg-light border px-3 py-1">
                <div>
                  <Typography variant="h5" style={{ color: "#078ece" }}>
                    Disability
                  </Typography>
                </div>
                {canEdit && (
                  <Button
                    variant="contained"
                    startIcon={<AddRoundedIcon />}
                    disabled={loading}
                    onClick={() => setShowDisabilityForm(true)}
                    size="small"
                  >
                    <span className="d-none d-md-inline mr-1">Add</span> new
                  </Button>
                )}
              </div>

              {userDisabilities.length === 0 ? (
                <NoResults />
              ) : (
                userDisabilities.map((disability, index) => (
                  <DisabilityItemCard
                    key={disability.id}
                    index={index}
                    disability={disability}
                    loading={loading}
                    deleteUserDisability={deleteUserDisability}
                    setSelectedDisability={setSelectedDisability}
                    setShowDisabilityForm={setShowDisabilityForm}
                    setLoadingDocument={setLoadingDocument}
                    updateUserFile={updateUserFile}
                    env={env}
                    canEdit={canEdit}
                  />
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {showDisabilityForm && (
        <DisabilityForm
          selectedDisability={selectedDisability}
          onShow={showDisabilityForm}
          onClose={() => {
            setShowDisabilityForm(false);
            setSelectedDisability(null);
          }}
        />
      )}

      <div
        className={`page-loading-void position-fixed ${
          loadingDocument ? "d-block" : "d-none"
        }`}
      >
        {loadingDocument && (
          <CircularProgress
            sx={{
              zIndex: (theme) => theme.zIndex.drawer + 1,
              color: "rgb(7, 142, 206)",
              position: "absolute",
              top: "50%",
              left: "50%",
              marginTop: "-12px",
              marginLeft: "-12px",
            }}
            size={40}
            thickness={4}
          />
        )}
      </div>
    </>
  );
};

const mapStateToProps = ({
  userProfile,
  env,
  loading,
  onWait,
  userDisabilities,
  lookup,
}) => ({
  userProfile,
  env,
  loading,
  onWait,
  userDisabilities,
  lookup,
});

export default connect(mapStateToProps, {
  getUserDisabilities,
  deleteUserDisability,
  getDisabilities,
  getDisabilityLevels,
  updateUserFile,
})(Disabilities);

const DisabilityItemCard = (props) => {
  const {
    index,
    env,
    loading,
    disability,
    deleteUserDisability,
    setSelectedDisability,
    setShowDisabilityForm,
    setLoadingDocument,
    updateUserFile,
    canEdit,
  } = props;

  const [onViewDocument, setOnViewDocument] = useState(false);
  const [onDelete, setOnDelete] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Card
        className={`mb-2 rounded hoverable-card `}
        elevation={0}
        variant="outlined"
        style={{
          backgroundColor: "#fff",
          border: "none",
        }}
      >
        <CardHeader
          title={<Typography>{disability?.disability?.name}</Typography>}
          subheader={
            disability.disabilityId !== 1 && (
              <>
                Level of disability:{" "}
                <span className="btn-link">
                  {disability?.disabilityLevel?.name}{" "}
                </span>
                {!!disability.updatedOn && (
                  <p
                    className="small text-muted mb-0 rounded-3 mt-2 mr-2 float-end"
                    style={{
                      fontSize: "11px",
                    }}
                  >
                    Updated on, {moment(disability.updatedOn).format("ll")}
                  </p>
                )}
                {!disability.updatedOn && (
                  <p
                    className="small text-muted mb-0 rounded-3 mt-2 mr-2 float-end"
                    style={{
                      fontSize: "11px",
                    }}
                  >
                    Created on, {moment(disability.createdOn).format("ll")}
                  </p>
                )}
              </>
            )
          }
          action={
            <>
              <div
                className="d-flex flex-row align-items-center "
                style={{ borderRadius: "4px" }}
              >
                {disability.disabilityId !== 1 && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (disability.docReferenceId) {
                        setOnViewDocument(true);
                      } else {
                        updateUserFile(
                          disability,
                          "CHANGE_DISABILITY_FILE",
                          disability.userId + "-" + disability.disability.name,
                          setOnViewDocument,
                          setLoadingDocument
                        );
                      }
                    }}
                  >
                    <FilePresentIcon
                      // fontSize="large"
                      style={{
                        color: !!disability.docReferenceId
                          ? "#fe4066"
                          : "#c57c7cb8",
                      }}
                    />
                  </IconButton>
                )}
                {disability.locked && (
                  <IconButton
                    disableRipple
                    size="small"
                    className=""
                    aria-label="settings"
                  >
                    <LockIcon />
                  </IconButton>
                )}
                {(!disability.locked || env !== "prod") && canEdit && (
                  <>
                    <IconButton
                      size="small"
                      // className="ml-n2"
                      onClick={handleOpenMenu}
                      aria-label="settings"
                    >
                      <Badge color="info">
                        <span className="material-icons">more_vert</span>
                      </Badge>
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      id="account-menu"
                      open={open}
                      onClose={handleCloseMenu}
                      PaperProps={{
                        elevation: 0,
                        sx: {
                          overflow: "visible",
                          filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                          mt: 1.5,
                          "& .MuiAvatar-root": {
                            width: 32,
                            height: 32,
                            ml: -0.5,
                            mr: 2,
                          },
                          "&:before": {
                            content: '""',
                            display: "block",
                            position: "absolute",
                            top: 0,
                            right: 15,
                            width: 10,
                            height: 10,
                            bgcolor: "background.paper",
                            transform: "translateY(-50%) rotate(45deg)",
                            zIndex: 0,
                          },
                        },
                      }}
                      transformOrigin={{ horizontal: "right", vertical: "top" }}
                      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                    >
                      <span>
                        <MenuItem
                          disabled={loading}
                          onClick={() => {
                            handleCloseMenu();
                            setSelectedDisability(disability);
                            setShowDisabilityForm(true);
                          }}
                          className="text-dark font-weight-light"
                        >
                          <span className="material-icons mr-1">edit_note</span>
                          Edit
                        </MenuItem>
                      </span>

                      <span>
                        <MenuItem
                          disabled={loading}
                          onClick={() => {
                            handleCloseMenu();
                            setOnDelete(true);
                          }}
                          className="text-danger font-weight-light"
                        >
                          <span className="material-icons mr-1">remove</span>{" "}
                          Remove
                        </MenuItem>
                      </span>
                    </Menu>
                  </>
                )}
              </div>
            </>
          }
        />
      </Card>

      <Divider variant="fullWidth" />
      <Divider variant="fullWidth" />

      {onDelete && (
        <AlertDialog
          status={onDelete}
          handleClose={() => setOnDelete(false)}
          handleConfirm={() =>
            deleteUserDisability(disability.id, () => setOnDelete(false))
          }
          title="Delete qualification"
          desc="Are your sure you want to delete this disability?"
          disabled={loading}
        />
      )}

      {onViewDocument && (
        <PreviewPdfUrl
          showDocument={onViewDocument}
          setShowDocument={() => setOnViewDocument(false)}
          document={{
            name: `disability document`,
            url:
              process.env.REACT_APP_FILES_URL +
              "/preview/" +
              disability.docReferenceId,
          }}
        />
      )}
    </>
  );
};
